/**
 * Test-only NATS seam for Playwright.
 *
 * Playwright pages wire `createRealtimeHook` to a `MockNatsAdapter` and never open a
 * WebSocket, so a spec cannot drive realtime traffic on its own. This module hangs a
 * handle for that in-page adapter off `window` so one can: `deliver` injects an event
 * as if it had arrived from NATS, and an optional `window.__realtimeTestRelay__` is
 * notified of everything the page publishes. Injection enters at the adapter boundary,
 * so everything above the socket is the production path.
 *
 * Kept out of the package barrel so app code cannot pick it up by accident: the apps that
 * use it import the `@web-monorepo/realtime/playwright-bridge` subpath statically and pass it
 * their test-only `MockNatsAdapter`, which is null outside testing. `deliver` reaches nothing
 * but the page's own mock adapter, so even where it is installed it cannot touch a real
 * connection or another user's data.
 *
 * See "Cross-device Playwright coverage" in `ai/rules/nats-realtime.md` for the rules, and
 * `NatsRelay` in `@web-monorepo/playwright` for the Playwright-side counterpart.
 */

import type { MockNatsAdapter } from "./mock-nats-adapter";
import type { RealtimePayload } from "./types";

/** Name of the handle `installRealtimeTestBridge` hangs off `window`. */
export const REALTIME_TEST_BRIDGE_KEY = "__realtimeTestBridge__";

/** Name of the optional Playwright-installed relay the bridge notifies on publish. */
export const REALTIME_TEST_RELAY_KEY = "__realtimeTestRelay__";

export interface RealtimeTestBridge {
  /**
   * Deliver `message` to this page's subscribers of `topic`, exactly as an inbound
   * NATS message would be. Deliberately does NOT notify the relay, so an injected
   * message cannot echo back to the page that relayed it.
   */
  deliver(topic: string, message: RealtimePayload): void;
  /** Subjects this page currently subscribes to. */
  subscriptions(): string[];
}

type RelayFn = (topic: string, payload: RealtimePayload) => void;

/**
 * `window` as the bridge and its callers see it. Exported so the Playwright-side
 * helper and the unit test can reach the two keys without casting through `unknown`.
 * A local type rather than a `declare global` augmentation, which would put these test-only
 * keys on `Window` for every importer of the realtime package.
 */
export type RealtimeBridgeWindow = Window & {
  [REALTIME_TEST_BRIDGE_KEY]?: RealtimeTestBridge;
  [REALTIME_TEST_RELAY_KEY]?: RelayFn;
};

/**
 * Publish the page's outbound realtime traffic to `window.__realtimeTestRelay__` (when a
 * spec installed one) and expose `window.__realtimeTestBridge__`.
 *
 * Takes the app's `mockAdapter`, which is null outside testing — so the gate is here rather
 * than copy-pasted into every call site, and a production page cannot install the bridge even
 * if something calls this. Not reachable from the barrel, so importing it is the opt-in.
 */
export function installRealtimeTestBridge(adapter: MockNatsAdapter | null): void {
  if (!adapter || typeof window === "undefined") {
    return;
  }

  // No cast needed: the type only adds optional keys to `Window`.
  const bridgeWindow: RealtimeBridgeWindow = window;
  // The adapter notifies publish listeners synchronously, so this flag is what keeps an
  // injected message from being relayed straight back to the page that sent it.
  // Subscribers run later, on a microtask, so anything a handler publishes in response
  // (teach's peer host answers a ping with a pong) still reaches the relay as the real
  // outbound traffic it is.
  let delivering = false;

  adapter.addPublishListener((topic, payload) => {
    if (delivering) return;
    bridgeWindow[REALTIME_TEST_RELAY_KEY]?.(topic, payload);
  });

  bridgeWindow[REALTIME_TEST_BRIDGE_KEY] = {
    deliver(topic, message) {
      delivering = true;
      try {
        adapter.publish(topic, message);
      } finally {
        delivering = false;
      }
    },
    subscriptions: () => adapter.getSubscriptions(),
  };
}
